import { Card, Checkbox, FormControl, FormHelperText, Input, MenuItem, Radio, RadioGroup, Select, Slider, styled, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react'
import CustomButton from '../../../common/CustomButton';
import activeImgae from '../../../assets/img/coupon/active.png';
import inActiveImgae from '../../../assets/img/coupon/inactive.png';
import GetTimeFormatted from '../../../helper/getTimeFormatted';
import { useCouponContextV2 } from '../../../context/CouponContextV2';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from "dayjs";

const PrettoSlider = styled(Slider)({
  color: '#2b1de8',
  height: 2,

  '& .MuiSlider-track': {
    border: 'none',
  },
  '& .MuiSlider-thumb': {
    height: 12,
    width: 12,
    backgroundColor: '#fff',
    border: '2px solid currentColor',
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: 'inherit',
    },
    '&::before': {
      display: 'none',
    },
  },
  '& .MuiSlider-valueLabel': {
    lineHeight: 1.2,
    fontSize: 12,
    background: 'unset',
    padding: 0,
    width: 32,
    height: 32,
    borderRadius: '50% 50% 50% 0',
    backgroundColor: '#2b1de8',
    transformOrigin: 'bottom left',
    transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
    '&::before': { display: 'none' },
    '&.MuiSlider-valueLabelOpen': {
      transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
    },
    '& > *': {
      transform: 'rotate(45deg)',
    },
  },
});

export default function CreateCouponSection({couponDealOption, selectCouponDealOption, setValidFor, errors, setError, couponStatus = 'draft', originalValidTill, isPopin = false}) {

  // Get context to access timeframe
  const { couponState } = useCouponContextV2();
  
  // Date picker state
  const [selectedDate, setSelectedDate] = useState(null);

  // Initialize selectedDate from persisted state
  useEffect(() => {
    if (couponDealOption.selectedExpiryDate) {
      setSelectedDate(dayjs(couponDealOption.selectedExpiryDate));
    }
  }, [couponDealOption.selectedExpiryDate]);

  const calculateExpirationFromDays = (days) => {
    if (days === null || days === undefined || days <= 0) return null;
    
    if (days >= 1 && days <= 6) {
      return { button: 3, option: days }; // SPECIAL OCCASIONS 1-6 DAYS
    } else if (days >= 7 && days <= 21) {
      return { button: 1, option: Math.ceil(days / 7) }; // SHORT TERM 1-3 WEEKS
    } else if (days > 21 && days <= 330) {
      return { button: 2, option: Math.ceil(days / 30) }; // MID TERM 1-11 MONTHS  
    } else if (days > 330 && days <= 730) {
      return { button: 4, option: Math.ceil(days / 365) }; // LONG TERM  : 1-2 YEARS
    }
    return null;
  };

  // Helper: Parse timeframe string to days
  const parseTimeframeToDays = (timeframe) => {
    if (!timeframe) return null;
    
    const match = timeframe.match(/^(\d+)\s*(day|week|month|year)s?$/i);
    if (!match) return null;

    const value = parseInt(match[1]);
    const unit = match[2].toLowerCase();

    switch (unit) {
      case 'day': return value;
      case 'week': return value * 7;
      case 'month': return value * 30;
      case 'year': return value * 365;
      default: return null;
    }
  };

  // Calculate expiration range from createdAt and validTill (for approved coupons)
  const calculateExpirationRange = (createdAt, validTill) => {
    if (!createdAt || !validTill) return null;
    
    try {
      const startDate = new Date(createdAt);
      const endDate = new Date(validTill);
      const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      
      return calculateExpirationFromDays(daysDiff);
    } catch (error) {
      console.error("Error calculating expiration range from dates:", error);
      return null;
    }
  };

  // Calculate validTill based on current expiration settings

  // Format expiry date for display - includes live calculation for new coupons
  const getExpiryDateDisplay = () => {
    // If coupon doesn't expire, show "No expiry"
    if (couponDealOption.couponExpiration) {
      return "No expiry";
    }
    
    // For pending/approved coupons, show original validTill
    if (originalValidTill) {
      return GetTimeFormatted(originalValidTill, "DD/MM/YYYY");
    }
    
    // For non-approved coupons, show validTill if available (backend handles calculation)
    // But only if we're not in extend expiry mode (to preserve original expiration display)
    if (couponDealOption.validTill && couponStatus !== 'approved' && !couponDealOption.extendExpiryTime) {
      return GetTimeFormatted(couponDealOption.validTill, "DD/MM/YYYY");
    }
    
    // For draft coupons, use validTill if available
    if (couponDealOption.validTill && !couponDealOption.extendExpiryTime) {
      return GetTimeFormatted(couponDealOption.validTill, "DD/MM/YYYY");
    }

    // For pending coupons, calculate expiry date from createdAt + validFor
    if (couponStatus === 'pending' && couponState?.createdAt && couponState?.validFor) {
      try {
        const createdAt = new Date(couponState.createdAt);
        const expirationDate = new Date(createdAt);
        expirationDate.setDate(createdAt.getDate() + couponState.validFor);
        return GetTimeFormatted(expirationDate, "DD/MM/YYYY");
      } catch (error) {
        console.error("Error calculating expiry date from validFor:", error);
      }
    }

    // For new coupon creation, calculate live expiration date based on current settings
    if (couponStatus === 'draft' && couponDealOption.expirationButton && couponDealOption.expirationOption) {
      const currentDate = new Date();
      const expirationDate = new Date(currentDate);
      
      // Calculate expiration based on selected button and option
      if (couponDealOption.expirationButton === 1) { // SHORT TERM - weeks
        expirationDate.setDate(currentDate.getDate() + (couponDealOption.expirationOption * 7));
      } else if (couponDealOption.expirationButton === 2) { // MID TERM - months
        expirationDate.setMonth(currentDate.getMonth() + couponDealOption.expirationOption);
      } else if (couponDealOption.expirationButton === 3) { // SPECIAL OCCASIONS - days
        expirationDate.setDate(currentDate.getDate() + couponDealOption.expirationOption);
      } else if (couponDealOption.expirationButton === 4) { // LONG TERM - years
        expirationDate.setFullYear(currentDate.getFullYear() + couponDealOption.expirationOption);
      }
      
      return GetTimeFormatted(expirationDate, "DD/MM/YYYY");
    }
    
    return null;
  };

  // Calculate new expiry date based on current selection
  const getNewExpiryDate = () => {
    if (couponDealOption.couponExpiration || !couponDealOption.extendExpiryTime) return null;
    
    // If date picker is used, show the selected date
    if (couponDealOption.selectedExpiryDate) {
      return GetTimeFormatted(couponDealOption.selectedExpiryDate, "DD/MM/YYYY");
    }
    
    // Check if coupon is actually expired (past its validTill date)
    const isActuallyExpired = () => {
      if (originalValidTill) {
        const expiryDate = new Date(originalValidTill);
        const today = new Date();
        return expiryDate < today;
      } else if (couponState?.createdAt && couponState?.validFor) {
        const createdAt = new Date(couponState.createdAt);
        const expiryDate = new Date(createdAt);
        expiryDate.setDate(createdAt.getDate() + couponState.validFor);
        const today = new Date();
        return expiryDate < today;
      }
      return false;
    };

    // Calculate from today + additional days (for actually expired coupons)
    if (isActuallyExpired() && couponDealOption.additionalDays) {
      const additionalDays = parseInt(couponDealOption.additionalDays);
      if (additionalDays > 0) {
        const today = new Date();
        const newDate = new Date(today);
        newDate.setDate(today.getDate() + additionalDays);
        const formattedDate = GetTimeFormatted(newDate, "DD/MM/YYYY");
        return formattedDate;
      }
    }
    
    // Calculate from original validTill + additional days
    if (originalValidTill) {
      const originalDate = new Date(originalValidTill);
      const additionalDays = couponDealOption.additionalDays ? parseInt(couponDealOption.additionalDays) : 0;
      
      if (additionalDays > 0) {
        const newDate = new Date(originalDate);
        newDate.setDate(originalDate.getDate() + additionalDays);
        const formattedDate = GetTimeFormatted(newDate, "DD/MM/YYYY");
        return formattedDate;
      }
    }
    
    // Calculate from createdAt + validFor + additional days (for pending coupons)
    if (couponState?.createdAt && couponState?.validFor && couponDealOption.additionalDays) {
      const additionalDays = parseInt(couponDealOption.additionalDays);
      if (additionalDays > 0) {
        const createdAt = new Date(couponState.createdAt);
        const originalExpiryDate = new Date(createdAt);
        originalExpiryDate.setDate(createdAt.getDate() + couponState.validFor);
        
        const newDate = new Date(originalExpiryDate);
        newDate.setDate(originalExpiryDate.getDate() + additionalDays);
        const formattedDate = GetTimeFormatted(newDate, "DD/MM/YYYY");
        return formattedDate;
      }
    }
    
    return null;
  };

  const getSelectedTimeValue = () => {
    if (couponDealOption.couponExpiration) return null;
    
    const timeUnit = couponDealOption.expirationButton == 1 ? 'weeks' : couponDealOption.expirationButton == 2 ? 'months' : couponDealOption.expirationButton == 3 ? 'days' : 'years';
    
    return `${couponDealOption.expirationOption || 1} ${timeUnit}`;
  };

  const getExpirationUiState = (couponData, currentCouponStatus, currentCouponExpiration) => {

    let expirationButton = null;
    let expirationOption = null;
    let couponExpiration = currentCouponExpiration || false;

    // Priority 1: Explicit "Coupon does not expire" checkbox
    if (couponExpiration) {
      return { expirationButton: null, expirationOption: null, couponExpiration: true };
    }

    // Priority 2: For APPROVED coupons, use validTill calculation
    if (currentCouponStatus === 'approved' && couponData?.createdAt && couponData?.validTill) {
      const range = calculateExpirationRange(couponData.createdAt, couponData.validTill);
      if (range) {
        expirationButton = range.button;
        expirationOption = range.option;
        return { expirationButton, expirationOption, couponExpiration: false };
      }
    }

    // Priority 3: For NON-APPROVED coupons, use validFor (this is YOUR 120 days!)
    if (currentCouponStatus !== 'approved' && couponData?.validFor !== null && couponData?.validFor !== undefined) {
      const range = calculateExpirationFromDays(couponData.validFor);
      if (range) {
        expirationButton = range.button;
        expirationOption = range.option;
        return { expirationButton, expirationOption, couponExpiration: false };
      }
    }

    // Priority 4: Fallback to timeframe string parsing
    if (currentCouponStatus !== 'approved' && couponData?.timeframe) {
      const daysFromTimeframe = parseTimeframeToDays(couponData.timeframe);
      if (daysFromTimeframe) {
        const range = calculateExpirationFromDays(daysFromTimeframe);
        if (range) {
          expirationButton = range.button;
          expirationOption = range.option;
          return { expirationButton, expirationOption, couponExpiration: false };
        }
      }
    }

    return { expirationButton: null, expirationOption: null, couponExpiration: false };
  };

  useEffect(() => {
    // Enhanced check - handle both coupon state and direct props
    const couponData = couponState || {};
    const couponId = couponData.id || couponData.couponId;
    
    // Only proceed if we have some form of coupon identification
    if (!couponId) {
      return;
    }

    // Use our master function to determine the correct UI state
    const newExpirationState = getExpirationUiState(
      couponData, // Raw API data: { validFor: 2, timeframe: "2 days", createdAt: "...", validTill: null, ... }
      couponStatus, // "draft", "approved", etc.
      couponDealOption.couponExpiration // Current checkbox state
    );

    // Only update if there's an actual change to prevent infinite loops
    const hasSignificantChange = (
      newExpirationState.expirationButton !== couponDealOption.expirationButton ||
      newExpirationState.expirationOption !== couponDealOption.expirationOption ||
      newExpirationState.couponExpiration !== couponDealOption.couponExpiration
    );

    if (hasSignificantChange) {


      selectCouponDealOption({
        ...couponDealOption,
        expirationButton: newExpirationState.expirationButton,
        expirationOption: newExpirationState.expirationOption,
        couponExpiration: newExpirationState.couponExpiration
      });
    }
  }, [
    couponState?.id, // Only when coupon ID changes (new coupon loaded)
    couponState?.couponId, // Alternative coupon ID field
    couponState?.validFor, // When validFor days change  
    couponState?.timeframe, // When timeframe string changes
    couponState?.createdAt, // When createdAt changes
    couponState?.validTill, // When validTill changes
    couponStatus, // When coupon status changes
    couponDealOption.couponExpiration, // When user toggles checkbox
  ]);

  // Initialize validTill in create mode - but keep it null for draft mode
  useEffect(() => {
    if (!couponDealOption.validTill && !couponDealOption.couponExpiration && !couponDealOption.createdAt) {
      selectCouponDealOption({
        ...couponDealOption,
        validTill: null // Keep null during draft mode
      });
    }
  }, []);

  const handleChange = (event) => {

    selectCouponDealOption({
      ...couponDealOption,
      selectedDealOptions: event.target.value
    })

  };

  const handleSelectChange = (event) => {
    selectCouponDealOption({
      ...couponDealOption,
      third: {
        ...couponDealOption.third,
        dealVal2: event.target.value
      }
    })
  };

  // Handle expiration button click - reset previous settings
  const handleExpirationButtonClick = (buttonNumber) => {
    const updatedOption = {
      ...couponDealOption,
      expirationButton: buttonNumber,
      expirationOption: 1, // Reset to default value
      extendExpiryTime: false, // Reset extend expiry time
      additionalDays: '' // Reset additional days
    };
    
    // Only calculate validTill for pending coupons, keep null for draft mode
    if (couponStatus !== 'draft') {
      // For approved/pending coupons, don't calculate validTill here
      // It will be calculated by the backend when pending
      updatedOption.validTill = null;
    } else {
      updatedOption.validTill = null; // Keep null for draft mode
    }
    
    selectCouponDealOption(updatedOption);
  };

  // Handle additional days input change with validation
  const handleAdditionalDaysChange = (e) => {
    const value = e.target.value;
    
    // Allow empty string, positive numbers, and reasonable limits
    if (value === '' || (parseInt(value) > 0 && parseInt(value) <= 10000)) {
      const updatedOption = {
        ...couponDealOption,
        additionalDays: value,
        selectedExpiryDate: null // Clear date picker when using days input
      };
      
      // For non-draft coupons, calculate exact valid_till date when additional days are added
      if (couponStatus !== 'draft' && value && parseInt(value) > 0) {
        const additionalDays = parseInt(value);
        
        // Check if coupon is actually expired (past its validTill date)
        const isActuallyExpired = () => {
          if (originalValidTill) {
            const expiryDate = new Date(originalValidTill);
            const today = new Date();
            return expiryDate < today;
          } else if (couponState?.createdAt && couponState?.validFor) {
            const createdAt = new Date(couponState.createdAt);
            const expiryDate = new Date(createdAt);
            expiryDate.setDate(createdAt.getDate() + couponState.validFor);
            const today = new Date();
            return expiryDate < today;
          }
          return false;
        };

        // Calculate new expiry date by adding additional days to original valid_till or createdAt + validFor
        if (isActuallyExpired()) {
          // Case 1: Actually expired coupon - use today as base date
          const today = new Date();
          const newExpiryDate = new Date(today);
          newExpiryDate.setDate(today.getDate() + additionalDays);
          updatedOption.validTill = newExpiryDate.toISOString();
          
          // Calculate validFor from today to new expiry date
          const daysDifference = Math.ceil((newExpiryDate - today) / (1000 * 60 * 60 * 24));
          updatedOption.validFor = daysDifference;
          setValidFor(daysDifference);
        } else if (originalValidTill) {
          // Case 2: Has original validTill (approved coupons)
          const originalDate = new Date(originalValidTill);
          const newExpiryDate = new Date(originalDate);
          newExpiryDate.setDate(originalDate.getDate() + additionalDays); 
          updatedOption.validTill = newExpiryDate.toISOString();
          
          // Calculate validFor from today to new expiry date
          const today = new Date();
          const daysDifference = Math.ceil((newExpiryDate - today) / (1000 * 60 * 60 * 24));
          updatedOption.validFor = daysDifference;
          setValidFor(daysDifference);
        } else if (couponState?.createdAt && couponState?.validFor) {
          // Case 3: Pending coupon with validFor (no validTill)
          const createdAt = new Date(couponState.createdAt);
          const originalExpiryDate = new Date(createdAt);
          originalExpiryDate.setDate(createdAt.getDate() + couponState.validFor);
          
          const newExpiryDate = new Date(originalExpiryDate);
          newExpiryDate.setDate(originalExpiryDate.getDate() + additionalDays);
          updatedOption.validTill = newExpiryDate.toISOString();
          
          // Calculate validFor from today to new expiry date
          const today = new Date();
          const daysDifference = Math.ceil((newExpiryDate - today) / (1000 * 60 * 60 * 24));
          updatedOption.validFor = daysDifference;
          setValidFor(daysDifference);
        }
      }
      
      selectCouponDealOption(updatedOption);
      setSelectedDate(null); // Clear date picker
    }
  };

  // Handle date picker change
  const handleDateChange = (date) => {
    const today = dayjs();
    const selectedDayjs = dayjs(date);
    
    // Validate that selected date is not before today
    if (selectedDayjs.isBefore(today, 'day')) {
      setError('expiryDate', true);
      return;
    }
    
    setError('expiryDate', false);
    setSelectedDate(selectedDayjs);
    
    // Fix timezone issue by setting time to end of day
    const endOfDay = selectedDayjs.endOf('day');
    
    const updatedOption = {
      ...couponDealOption,
      selectedExpiryDate: endOfDay.toISOString(),
      additionalDays: '', // Clear days input when using date picker
    };
    
    // Only update validTill and validFor if we're in extend expiry mode
    if (couponDealOption.extendExpiryTime) {
      updatedOption.validTill = endOfDay.toISOString();
      
      // Calculate validFor as selected date - today
      const daysDifference = selectedDayjs.diff(today, 'day') + 1; // +1 to include the selected day
      updatedOption.validFor = daysDifference;
      setValidFor(daysDifference);
    }
    
    selectCouponDealOption(updatedOption);
  };

  return (
    <div className='create-coupon-section-v2'>
      <div className='options-container'>
        <Card sx={{ backgroundColor: 'white', width: '100%', padding: '1rem 2rem' }}>
          <div className='heading-text-container'>
            <span className='title'>Coupon Deal</span>
            <span className='subtitle'>Select and custom your deal</span>
          </div>

          <div className='option-sub-container '>
            <RadioGroup
              aria-labelledby="demo-controlled-radio-buttons-group"
              name="controlled-radio-buttons-group"
              value={couponDealOption.selectedDealOptions}
              onChange={handleChange}
            >

              <div className={couponDealOption.selectedDealOptions == 0 ? 'coupon-deal-option-container ' : 'coupon-deal-option-container inactive-option'}>

                <Radio
                  overlay
                  value={0}
                  sx={{}}
                  slotProps={{
                    action: ({ checked }) => ({
                      sx: (theme) => ({
                        ...(checked && {
                          inset: -1,
                          border: '2px solid',
                          borderColor: theme.vars.palette.primary[500],
                        }),
                      }),
                    }),
                  }}
                />
                <div className='img-block'>

                  <img src={couponDealOption.selectedDealOptions == 0 ? activeImgae : inActiveImgae} />

                  <div className='img-block-content'>
                    <div className='img-deal' style={{ fontSize: '12px', maxWidth: '60px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{couponDealOption.first.dealVal1}</div>
                    <div className='img-deal' style={{ fontSize: '12px', maxWidth: '60px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{couponDealOption.first.dealVal2}</div>
                    <div className='img-dealunit' style={{ fontSize: '10px' }}>FREE</div>
                  </div>

                </div>

                <FormControl variant="standard" sx={{ m: 1, mt: 3, width: '5ch', marginRight: '50px' }}>
                  <Input
                    id="standard-adornment-weight"
                    onChange={(e) => selectCouponDealOption({
                      ...couponDealOption,
                      first: {
                        ...couponDealOption.first,
                        dealVal1: e.target.value
                      }
                    })}
                    value={couponDealOption.first.dealVal1}
                    placeholder='1'
                    aria-describedby="standard-weight-helper-text"
                    inputProps={{
                      'aria-label': 'weight',
                    }}
                    onFocus={() => setError('couponDeal', false)}
                  />
                  <FormHelperText id="standard-weight-helper-text">Max 5 characters</FormHelperText>
                </FormControl>

                <FormControl variant="standard" sx={{ m: 1, mt: 3, width: '10ch', marginRight: "50px" }}>
                  <Input
                    id="standard-adornment-weight"
                    onChange={(e) => selectCouponDealOption({
                      ...couponDealOption,
                      first: {
                        ...couponDealOption.first,
                        dealVal2: e.target.value
                      }
                    })}
                    value={couponDealOption.first.dealVal2}
                    placeholder='Chicken'
                    aria-describedby="standard-weight-helper-text"
                    inputProps={{
                      'aria-label': 'weight',
                    }}
                    onFocus={() => setError('couponDeal', false)}
                  />
                  <FormHelperText id="standard-weight-helper-text">Max 10 characters</FormHelperText>
                </FormControl>

                <span>FREE</span>
              </div>

              <div className={couponDealOption.selectedDealOptions == 1 ? 'coupon-deal-option-container ' : 'coupon-deal-option-container inactive-option'}>

                <Radio
                  overlay
                  value={1}
                  sx={{}}
                  slotProps={{
                    action: ({ checked }) => ({
                      sx: (theme) => ({
                        ...(checked && {
                          inset: -1,
                          border: '2px solid',
                          borderColor: theme.vars.palette.primary[500],
                        }),
                      }),
                    }),
                  }}
                />
                <div className='img-block'>

                  <img src={couponDealOption.selectedDealOptions == 1 ? activeImgae : inActiveImgae} />

                  <div className='img-block-content'>
                    <div className='img-deal' style={{ fontSize: '12px', maxWidth: '60px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{couponDealOption.second.dealVal1}%</div>
                    <div className='img-dealunit' style={{ fontSize: '10px' }}>OFF</div>
                  </div>

                </div>

                <FormControl variant="standard" sx={{ m: 1, mt: 3, width: '5ch', marginRight: '50px' }}>
                  <Input
                    onChange={(e) => selectCouponDealOption({
                      ...couponDealOption,
                      second: {
                        ...couponDealOption.second,
                        dealVal1: e.target.value
                      }
                    })}

                    value={couponDealOption.second.dealVal1}
                    id="standard-adornment-weight"
                    placeholder='30'
                    aria-describedby="standard-weight-helper-text"
                    inputProps={{
                      'aria-label': 'weight',
                    }}
                    onFocus={() => setError('couponDeal', false)}
                  />
                  <FormHelperText id="standard-weight-helper-text">Max 5 characters</FormHelperText>
                </FormControl>

                <span>% OFF</span>
              </div>
              <div className={couponDealOption.selectedDealOptions == 2 ? 'coupon-deal-option-container ' : 'coupon-deal-option-container inactive-option'}>

                <Radio
                  overlay
                  value={2}
                  sx={{}}
                  slotProps={{
                    action: ({ checked }) => ({
                      sx: (theme) => ({
                        ...(checked && {
                          inset: -1,
                          border: '2px solid',
                          borderColor: theme.vars.palette.primary[500],
                        }),
                      }),
                    }),
                  }}
                />
                <div className='img-block'>

                  <img src={couponDealOption.selectedDealOptions == 2 ? activeImgae : inActiveImgae} />


                  <div className='img-block-content'>
                    <div className='img-deal' style={{ fontSize: '12px', maxWidth: '60px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{couponDealOption.third.dealVal1 ? `-${couponDealOption.third.dealVal1}` : ''}</div>
                    <div className='img-dealunit' style={{ fontSize: '10px' }}>{couponDealOption.third.dealVal2.toUpperCase()}</div>
                  </div>

                </div>

                <FormControl variant="standard" sx={{ m: 1, mt: 3, width: '5ch', marginRight: '80px' }}>
                  <Input
                    onChange={(e) => selectCouponDealOption({
                      ...couponDealOption,
                      third: {
                        ...couponDealOption.third,
                        dealVal1: e.target.value
                      }
                    })}
                    value={couponDealOption.third.dealVal1}
                    id="standard-adornment-weight"
                    aria-describedby="standard-weight-helper-text"
                    inputProps={{
                      'aria-label': 'weight',
                    }}
                    onFocus={() => setError('couponDeal', false)}
                  />
                  <FormHelperText id="standard-weight-helper-text">Max 5 characters</FormHelperText>

                </FormControl>

                <FormControl variant="standard" sx={{ m: 1, mt: 3, width: '8ch', marginRight: '50px' }}>
                  <Select
                    labelId="demo-select-small-label"
                    id="demo-select-small"
                    value={couponDealOption.third.dealVal2}
                    onChange={handleSelectChange}
                    defaultValue={'zar'}
                  >

                    <MenuItem value={'zar'}>ZAR</MenuItem>
                    <MenuItem value={'usd'}>USD</MenuItem>
                    <MenuItem value={'aud'}>AUD</MenuItem>
                    <MenuItem value={'inr'}>INR</MenuItem>
                  </Select>
                </FormControl>

              </div>

            </RadioGroup>
            <div className='coupon-deal-option-container'>
            </div>
          </div>

          {errors?.couponDeal?.show && <div className="coupon-error-text">{errors.couponDeal.message}</div>}
        </Card>
        <div className='option-sub-container'>
          <Card sx={{ backgroundColor: 'white', minHeight: '400px', padding: '1rem 2rem', display: 'flex', flexDirection: "column", gap: '1rem', alignItems: 'center', width: "100%" }}>
            <span className="title">
              {isPopin ? (
                <>Coupon expiration</>
              ) : getExpiryDateDisplay() ? (
                <>
                  Coupon expiration: <strong>{getExpiryDateDisplay()}</strong>
                  {couponDealOption.extendExpiryTime && !couponDealOption.couponExpiration && getNewExpiryDate() && (
                    <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                      New expiry: <strong>{getNewExpiryDate()}</strong>
                    </div>
                  )}
                </>
              ) : (
                <>Coupon expiration</>
              )}
            </span>

            {!isPopin && couponStatus === 'draft' && (
              <div className='multilple-button-container'>
                <CustomButton
                  disabled={couponDealOption.couponExpiration || (couponStatus !== 'draft' && couponDealOption.extendExpiryTime)}
                  style={couponDealOption.expirationButton == 1 ? { opacity: "1" } : { opacity: '.2' }}
                  onClick={() => handleExpirationButtonClick(1)} >Short term 1-3 weeks</CustomButton>
                <CustomButton
                  disabled={couponDealOption.couponExpiration || (couponStatus !== 'draft' && couponDealOption.extendExpiryTime)}
                  style={couponDealOption.expirationButton == 2 ? { opacity: "1" } : { opacity: '.2' }}
                  onClick={() => handleExpirationButtonClick(2)}>Mid term 1- 11 months</CustomButton>
                <CustomButton
                  disabled={couponDealOption.couponExpiration || (couponStatus !== 'draft' && couponDealOption.extendExpiryTime)}
                  style={couponDealOption.expirationButton == 3 ? { opacity: "1" } : { opacity: '.2' }}
                  onClick={() => handleExpirationButtonClick(3)}>Special occasions 1-6 days</CustomButton>
                <CustomButton
                  disabled={couponDealOption.couponExpiration || (couponStatus !== 'draft' && couponDealOption.extendExpiryTime)}
                  style={couponDealOption.expirationButton == 4 ? { opacity: "1" } : { opacity: '.2' }}
                  onClick={() => handleExpirationButtonClick(4)} >Long term 1-2 years</CustomButton>
              </div>
            )}

            {/* Show extend expiry options for coupons other then draft */}
            {!isPopin && (couponStatus !== 'draft') && (
              <div className='checkbox-text' style={{ width: '100%', marginBottom: '10px' }}>
                <Checkbox 
                  checked={couponDealOption.extendExpiryTime || false} 
                  disabled={couponDealOption.couponExpiration}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    const updatedOption = {
                      ...couponDealOption,
                      extendExpiryTime: isChecked,
                      additionalDays: isChecked ? (couponDealOption.additionalDays || '') : ''
                    };
                    
                    // If unchecking, reset validTill to original
                    if (!isChecked && originalValidTill) {
                      updatedOption.validTill = originalValidTill;
                    }
                    
                    selectCouponDealOption(updatedOption);
                  }}
                  sx={{
                    color: "#2b1de8",
                    '&.Mui-checked': {
                      color: "#2b1de8",
                    },
                    '&.Mui-disabled': {
                      color: '#ccc',
                    },
                  }}
                  inputProps={{
                    'aria-label': 'Extend expiry time',
                  }}
                />
                <span>Adjust days to expiry</span>
              </div>
            )}

            {!isPopin && (couponStatus !== 'draft') && couponDealOption.extendExpiryTime && !couponDealOption.couponExpiration && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
                <FormControl variant="standard" sx={{ width: '150px' }}>
                  <Input
                    type="number"
                    value={couponDealOption.additionalDays || ''}
                    onChange={handleAdditionalDaysChange}
                    placeholder="Additional days"
                    inputProps={{
                      min: 1,
                      max: 10000,
                      'aria-label': 'Additional days',
                    }}
                  />
                  <FormHelperText>Additional days (1-10,000)</FormHelperText>
                </FormControl>
                
                <span style={{ color: '#666', fontWeight: 'bold' }}>OR</span>
                
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={selectedDate}
                    onChange={handleDateChange}
                    minDate={dayjs()}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        variant: 'standard',
                        error: errors?.expiryDate?.show,
                        helperText: errors?.expiryDate?.show ? 'Date cannot be before today' : 'Select expiry date',
                        sx: { width: '200px' }
                      }
                    }}
                  />
                </LocalizationProvider>
              </div>
            )}

            {!isPopin && couponStatus === 'draft' && (
              <PrettoSlider aria-label="Temperature"
                value={couponDealOption.expirationOption ?? 1}
                onChange={(e, value) => {
                  const updatedOption = {
                    ...couponDealOption,
                    expirationOption: value
                  };
                  
                  // Only calculate validTill for pending coupons, keep null for draft mode
                  // But don't override validTill if we're in extend expiry mode
                  if (couponStatus !== 'draft') {
                    if (!couponDealOption.extendExpiryTime) {
                      // Don't calculate validTill here - backend handles it
                      updatedOption.validTill = null;
                    }
                    // If extendExpiryTime is true, keep the existing validTill (don't override it)
                  } else {
                    updatedOption.validTill = null; // Keep null for draft mode
                  }
                  
                  selectCouponDealOption(updatedOption);
                }}
                disabled={couponDealOption.couponExpiration || (couponStatus !== 'draft' && couponDealOption.extendExpiryTime)}
                valueLabelDisplay="auto"
                step={1}
                marks
                min={1}
                max={couponDealOption.expirationButton == 1 ? 3 : couponDealOption.expirationButton == 2 ? 11 : couponDealOption.expirationButton == 3 ? 6 : 2} />
            )}

            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              width: '100%',
              gap: '1rem'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}>
                <div className='checkbox-text'>
                   <Checkbox checked={couponDealOption.couponExpiration} disabled={isPopin} onChange={(e) => {
                     const updatedOption = {
                       ...couponDealOption,
                       couponExpiration: e.target.checked,
                       extendExpiryTime: e.target.checked ? false : couponDealOption.extendExpiryTime, // Disable extend expiry time if coupon doesn't expire
                       additionalDays: e.target.checked ? '' : couponDealOption.additionalDays // Clear additional days if coupon doesn't expire
                     };

                     // Handle validTill based on coupon status
                     if (e.target.checked) {
                       updatedOption.validTill = null; // Clear if coupon doesn't expire
                     } else {
                       // Only calculate validTill for pending coupons, keep null for draft mode
                       updatedOption.validTill = null; // Backend handles calculation
                     }

                     selectCouponDealOption(updatedOption);
                   }}
                    sx={{
                      color: "#2b1de8",
                      '&.Mui-checked': {
                        color: "#2b1de8",
                      },
                      '&.Mui-disabled': {
                        color: '#2b1de8',
                      },
                    }}
                    inputProps={{
                      'aria-label': 'Coupon does not expire',
                    }}
                  />
                  <span>Coupon does not expire</span>
                </div>

                {!couponDealOption.couponExpiration && !couponDealOption.extendExpiryTime && getSelectedTimeValue() && couponStatus === 'draft' && (
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#666',
                    fontWeight: '500'
                  }}>
                    {getSelectedTimeValue()}
                  </div>
                )}
              </div>
            </div>
          </Card>
          <Card sx={{ backgroundColor: 'white', height: '200px', width: "100%", padding: '1rem 2rem', display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'end' }}>
            <span className='title'>Coupon Description</span>

            <div className='text-container'>
              <TextField
                id="standard-multiline-static"
                value={couponDealOption.description}
                onChange={(e) => selectCouponDealOption({
                  ...couponDealOption,
                  description: e.target.value
                })}
                onFocus={() => setError('couponDescription', false)}
                fullWidth
                multiline
                rows={3}
                placeholder='Brief specific description of the deal to inform the audience what you are offering with the coupon.'
                variant="standard"
              />
              <img src={"https://admin.delivait.com/assets/img/description.png"}></img>
            </div>
            {errors?.couponDescription?.show && <div className="coupon-error-text">{errors.couponDescription.message}</div>}

          </Card>
        </div>
      </div>

    </div>
  )
}
